import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { modelTierLabel } from "@/components/Backgrounds/facetLabels";
import { ChevronDown } from "lucide-react";
import { Button, Progress } from "@/components/ui";
import { cn } from "@/lib/utils";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import { BackgroundsQueueStatus } from "@/services/bedrock/dtos/BackgroundAsset";
import { designKeys } from "@/services/bedrock/queryKeys";

const REFRESH_MS = 10_000;
const COLLAPSED_KEY = "backgroundQueue.collapsed";

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  } catch {
    // Storage can be blocked; the choice then lasts for this visit only.
  }
}

type Health = "idle" | "working" | "slowed" | "stopped";
type Model = BackgroundsQueueStatus["models"][keyof BackgroundsQueueStatus["models"]];

const HEALTH: Record<Health, { label: string; dot: string }> = {
  idle: { label: "All caught up", dot: "bg-emerald-500" },
  working: { label: "Working", dot: "bg-sky-500 animate-pulse motion-reduce:animate-none" },
  slowed: { label: "Slowed down", dot: "bg-amber-500" },
  stopped: { label: "Stopped", dot: "bg-red-500" },
};

const available = (model: Model) => model.enabled && !model.paused;
const tierName = (model: string) => modelTierLabel(model) ?? "Other";

export function queueHealth(status: BackgroundsQueueStatus): Health {
  const models = Object.values(status.models);
  if (models.length > 0 && !models.some(available)) return "stopped";
  if (models.some(m => m.enabled && m.paused)) return "slowed";
  return status.queued.interactive + status.queued.filler + status.running === 0 ? "idle" : "working";
}

function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** "less than a minute", "5 min", "2 h 10 min". */
export function waitedFor(iso: string, now = Date.now()): string {
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "less than a minute";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours} h ${rest} min` : `${hours} h`;
}

function tierState(model: Model): string {
  if (!model.enabled) return "Off";
  if (model.paused) return model.pausedUntil ? `Paused until ${clockTime(model.pausedUntil)}` : "Paused";
  return `${model.startsInWindow} of ${model.rpm} images`;
}

const Stat: React.FC<{ label: string; value: React.ReactNode; help: string; testId: string }> = ({
  label,
  value,
  help,
  testId,
}) => (
  <div className="flex flex-col gap-0.5" data-testid={testId}>
    <span className="text-xs font-medium text-slate-500">{label}</span>
    <span className="text-xl font-semibold text-slate-800">{value}</span>
    <span className="text-xs text-slate-500">{help}</span>
  </div>
);

/**
 * The image queue at a glance: whether it's healthy, what's being made and waiting, and how much
 * of each tier's per-minute limit is used.
 */
export const QueueStrip: React.FC = () => {
  const { data } = useQuery({
    queryKey: designKeys.backgrounds.queue,
    queryFn: () => BackgroundAssetServices.queue(),
    refetchInterval: REFRESH_MS,
  });
  const [collapsed, setCollapsed] = React.useState(readCollapsed);

  if (!data) return null;

  const toggle = () => {
    setCollapsed(!collapsed);
    writeCollapsed(!collapsed);
  };
  const waiting = data.queued.interactive + data.queued.filler;

  const health = queueHealth(data);
  const pausedTiers = Object.entries(data.models)
    .filter(([, model]) => model.enabled && model.paused)
    .map(([name]) => tierName(name));

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="queue-strip">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="text-sm font-semibold text-slate-800">Image queue</div>
        <span className="flex items-center gap-1.5 text-sm text-slate-700" data-testid="queue-health">
          <span className={cn("size-2 rounded-full", HEALTH[health].dot)} aria-hidden="true" />
          {HEALTH[health].label}
        </span>
        {health === "slowed" && (
          <span className="text-xs text-slate-500">
            {pausedTiers.join(" and ")} hit its per-minute limit. Images keep going on the other tier.
          </span>
        )}
        {health === "stopped" && (
          <span className="text-xs text-slate-500">No new images are being made until a tier is back.</span>
        )}
        {collapsed && (
          <span className="text-xs text-slate-500" data-testid="queue-summary">
            {data.running} creating · {waiting} waiting
          </span>
        )}
        <span className="ml-auto flex items-center gap-3">
          {!collapsed && <span className="text-xs text-slate-400">Updates every 10 seconds</span>}
          <Button size="sm" variant="ghost" onClick={toggle} aria-expanded={!collapsed} aria-controls="queue-details">
            {collapsed ? "Show details" : "Hide details"}
            <ChevronDown className={cn("transition-transform", !collapsed && "rotate-180")} aria-hidden="true" />
          </Button>
        </span>
      </div>

      {!collapsed && (
        <div id="queue-details">
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
            <Stat label="Creating now" value={data.running} help="Images being made right now" testId="queue-running" />
            <Stat
              label="Waiting for coaches"
              value={data.queued.interactive}
              help="From the tray and new designs. Always go first."
              testId="queue-coaches"
            />
            <Stat
              label="Background work"
              value={data.queued.filler}
              help="Seed runs, bulk jobs, and extra sizes. Only uses spare room."
              testId="queue-background"
            />
            <Stat
              label="Longest wait"
              value={data.oldestQueuedAt ? waitedFor(data.oldestQueuedAt) : "None"}
              help="How long the oldest request has waited"
              testId="queue-oldest"
            />
          </div>

          <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs font-medium text-slate-500">Used this minute</span>
            <div className="grid gap-x-6 gap-y-2 md:grid-cols-2">
              {Object.entries(data.models).map(([name, model]) => (
                <div key={name} className="flex items-center gap-3" data-testid={`queue-model-${name}`}>
                  <span className="w-14 shrink-0 text-sm font-medium text-slate-700">{tierName(name)}</span>
                  <Progress
                    value={
                      available(model) && model.rpm > 0 ? Math.min(100, (model.startsInWindow / model.rpm) * 100) : 0
                    }
                    className="flex-1"
                    aria-label={`${tierName(name)} used this minute`}
                  />
                  <span
                    className={cn(
                      "w-40 shrink-0 text-right text-xs",
                      available(model) ? "text-slate-600" : "font-medium text-amber-700"
                    )}
                  >
                    {tierState(model)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
